<script setup lang="ts">
import { OrderStatus } from '@/coreDomainsModule/entities/arrival';
import HelpIconWithModal from '@/corePagesModule/components/HelpIconWithModal.vue';
import { i18n } from '@/i18n';
import SvgLoader from '@/utils/SvgLoader.vue';
import { FilterStatusType } from '../entities/IOrderControl';

const t = i18n.t;

const props = defineProps<{
  filterStatus: Record<FilterStatusType, boolean>;
  recordCount: Record<FilterStatusType, number | undefined>;
}>();

const emits = defineEmits<{ (e: 'updateOrderStatusFilter', value: OrderStatus) }>();

/**
 * 表示非表示のアイコンを取得
 * @param value - 取得するアイコン
 */
const getEyeIconName = (value: string) => {
  return props.filterStatus[value] ? 'iconEyeEnabled' : 'iconEyeDisable';
};

/**
 * 0, undefinedなどを -- に変換したUI表示用数値を返す
 * @param value - 数値
 */
const getDisplayNumber = (value: number | undefined) => {
  return value || '--';
};
</script>
<template>
  <div class="order-status-filter-buttons">
    <div class="recommended-wrapper">
      <button
        class="order-status-button order-status-button--recommended"
        :class="{ 'order-status-button--inactive': !props.filterStatus.Recommended }"
        @click="emits('updateOrderStatusFilter', OrderStatus.Recommended)"
      >
        <div class="order-status-button__container">
          <p class="order-status-button__text">{{ t('推奨') }}</p>
          <p>{{ getDisplayNumber(recordCount.Recommended) }} {{ t('件') }}</p>
          <SvgLoader class="order-status-button__icon" :name="getEyeIconName('Recommended')" />
        </div>
      </button>
      <HelpIconWithModal>
        <div class="recommend-status-hint">
          <div class="recommend-status-hint__header">
            <span class="recommend-status-hint__header--title">
              {{ t('推奨の出現条件') }}
            </span>
          </div>
          <div class="recommend-status-hint__content">
            <span class="recommend-status-hint__content--description">
              {{ t('登録されている最新在庫日より180日先までに発注推奨が存在する場合、発注推奨が出現します。') }}
            </span>
          </div>
        </div>
      </HelpIconWithModal>
    </div>
    <button
      class="order-status-button order-status-button--approval-requested"
      :class="{ 'order-status-button--inactive': !props.filterStatus.ApprovalRequested }"
      @click="emits('updateOrderStatusFilter', OrderStatus.ApprovalRequested)"
    >
      <div class="order-status-button__container">
        <p class="order-status-button__text">{{ t('承認依頼') }}</p>
        <p>{{ getDisplayNumber(recordCount.ApprovalRequested) }} {{ t('件') }}</p>
        <SvgLoader class="order-status-button__icon" :name="getEyeIconName('ApprovalRequested')" />
      </div>
    </button>
    <button
      class="order-status-button order-status-button--approved"
      :class="{ 'order-status-button--inactive': !props.filterStatus.ApprovedAwaitingDeliveryDate }"
      @click="emits('updateOrderStatusFilter', OrderStatus.ApprovedAwaitingDeliveryDate)"
    >
      <div class="order-status-button__container">
        <p class="order-status-button__text">{{ t('承認済') }}</p>
        <p>{{ getDisplayNumber(recordCount.ApprovedAwaitingDeliveryDate) }} {{ t('件') }}</p>
        <SvgLoader class="order-status-button__icon" :name="getEyeIconName('ApprovedAwaitingDeliveryDate')" />
      </div>
    </button>
  </div>
</template>
<style lang="scss" scoped>
@import '@/assets/scss/mixins/tagMixin.scss';
.order-status-filter-buttons {
  display: grid;
  grid-template-columns: repeat(3, auto);
  gap: 8px;
  margin-left: 8px;
  align-items: center;
  *:not(.material-icons) {
    font-family: Noto Sans JP;
  }
}

.recommend-status-hint {
  display: grid;
  grid-template-rows: auto auto;
  gap: 8px;
  width: 22vw; // NOTE: 表示画面に合わせて調整する (画像挿入とかするなら、これ外す)
  min-width: 100%; // NOTE: ヒントモーダル側の最小幅に合わせる
  &__header {
    display: grid;
    grid-template-columns: auto;
    gap: 8px;
    align-items: center;
    &--title {
      text-align: center;
      font-size: $fontSizeM;
      font-weight: bold;
    }
  }
  &__content {
    display: grid;
    grid-template-rows: auto;
    gap: 8px;
    &--description {
      font-size: $fontSizeS;
    }
  }
}

.recommended-wrapper {
  display: grid;
  grid-template-columns: auto fit-content(100%);
  gap: 4px;
  align-items: center;
}

.order-status-button {
  display: inline-block;
  height: 22px;
  @include tag_md_mixin; // 2024/03/19 タグ用のスタイル参照

  &--recommended {
    @include tag_orange_mixin; // 2024/03/19 タグ用のスタイル参照
  }
  &--approval-requested {
    @include tag_pink_mixin; // 2024/03/19 タグ用のスタイル参照
  }
  &--approved {
    @include tag_violet_mixin; // 2024/03/19 タグ用のスタイル参照
  }
  &--inactive {
    opacity: 40%;
  }

  &__text {
    color: $semanticWhite;
    font-family: Inter;
    font-size: 12px;
    font-weight: 700;
    line-height: 12px;
    letter-spacing: 0em;
    text-align: center;
  }
  &__container {
    display: grid;
    grid-template-columns: auto auto 14px;
    align-items: center;
    justify-content: center;
    gap: 4px;
  }

  &__icon {
    display: block;
  }
}
</style>
